import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from "react";

const SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

/**
 * Renders a Cloudflare Turnstile widget (explicit render mode).
 *
 * Relies on the Turnstile script loaded globally in `_document.js`. Exposes a
 * `reset()` handle via ref so the parent can request a fresh, single-use token
 * after a failed/expired submit.
 */
const TurnstileWidget = forwardRef(function TurnstileWidget(
  { onVerify, onExpire, onError, theme = "light", action },
  ref
) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Keep the latest callbacks in refs so the widget only renders once but
  // always invokes the current handlers.
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  const onErrorRef = useRef(onError);

  useEffect(() => {
    onVerifyRef.current = onVerify;
    onExpireRef.current = onExpire;
    onErrorRef.current = onError;
  }, [onVerify, onExpire, onError]);

  const renderWidget = useCallback(() => {
    if (
      typeof window === "undefined" ||
      !window.turnstile ||
      !containerRef.current ||
      widgetIdRef.current !== null
    ) {
      return;
    }

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      theme,
      action,
      callback: (token) => onVerifyRef.current?.(token),
      "expired-callback": () => onExpireRef.current?.(),
      "error-callback": () => onErrorRef.current?.(),
    });
  }, [theme, action]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    if (window.turnstile) {
      renderWidget();
      return undefined;
    }

    // Script may still be loading; poll until the Turnstile API is available.
    const interval = setInterval(() => {
      if (window.turnstile) {
        clearInterval(interval);
        renderWidget();
      }
    }, 200);

    return () => clearInterval(interval);
  }, [renderWidget]);

  useEffect(() => {
    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // Widget may already be gone; ignore.
        }
        widgetIdRef.current = null;
      }
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      reset: () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current);
        }
      },
    }),
    []
  );

  if (!SITE_KEY) {
    if (process.env.NODE_ENV !== "production") {
      console.warn(
        "NEXT_PUBLIC_TURNSTILE_SITE_KEY is not set; the CAPTCHA widget will not render."
      );
    }
    return null;
  }

  return <div ref={containerRef} />;
});

export default TurnstileWidget;
